/**
 * Miniature + Titre — « **Le putaclic-o-mètre** » : le pattern, la physique et le barème.
 *
 * ⚠️ **SPEC-UI §5.4 est PÉRIMÉE pour ce jeu** (elle décrit un curseur sinusoïdal à arrêter,
 * donc du timing). L'avenant **A-3** en fait un jeu de **dosage**, et le complément **A-3.1**
 * en donne la physique — c'est cette dernière qui fait foi ici, point par point :
 *
 * 1. **L'instrument est indirect et bruité** : 2-3 curseurs, UNE seule aiguille combinée. Les
 *    poids sont **seedés et cachés** — il faut trianguler par micro-mouvements.
 * 2. **L'aiguille est vivante** : inertie (ressort amorti, donc retard et overshoot) et
 *    **tremblement dont l'amplitude croît avec l'agressivité du réglage**. Zone rouge de santé
 *    mentale : tremblement ×1,5.
 * 3. **Le chrono FIGE** : à T=0, snapshot de l'aiguille là où elle est, **tremblement compris**.
 *    Pas de validation anticipée — la compétence, c'est d'amener l'aiguille dans la zone ET de
 *    la stabiliser avant la fin.
 * 4. **Push-your-luck** : la frontière de la zone rouge « miniature mensongère » est invisible
 *    tant qu'on ne l'a pas franchie.
 * 5. **Scaling** : paliers bas = aiguille lente et sweet spot fixe ; puis drift, aiguille
 *    nerveuse, 3e curseur, et curseurs **couplés** aux hauts paliers.
 *
 * Assists re-mappés (A-3.1) : *Analytics addict* = jauge moins bruitée · *Miniature putaclic*
 * = zone sweet +20 % · *Plume d'or* = amortisseur (tremblement ÷2).
 */

import {
  draw,
  exigenceAtelier,
  generateAtelierPattern,
  uniformFloat,
  type GameState,
} from "@maxime/shared";

import type { PatternMiniatureApi, TimelineMiniature } from "../../api/atelier";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de départ — ✎ « à calibrer à la main en jouant » (avenant A-3)
// ─────────────────────────────────────────────────────────────────────────────

/** Chrono de base à E1, en ms. Famille `duree_tempo` (arbitrage A30) : divisé par le tempo. */
export const CHRONO_BASE_MS = 14_000;
/** Largeur TOTALE de la zone sweet à E1, en points d'aiguille (SPEC-UI §4.2 : 24,0). */
export const ZONE_BASE = 24;
/** Zone d'un sujet chaos : ×2/3 (SPEC-UI §4.2 : 16,0 à E1). */
export const ZONE_CHAOS = 2 / 3;
/** Centre du sweet spot, tiré dans [40 ; 75] (SPEC-UI §4.2, constante non scalée). */
export const CENTRE_MIN = 40;
export const CENTRE_MAX = 75;
/** Frontière **invisible** de la zone « miniature mensongère » (push-your-luck, A-3.1 point 4). */
export const MENSONGE_MIN = 82;
export const MENSONGE_MAX = 92;

/** Raideur du ressort à E1 (rad²/s) : plus c'est haut, plus l'aiguille est nerveuse. */
export const RAIDEUR_BASE = 9;
/** Amortissement à E1 : sous la valeur critique (2√k), donc overshoot garanti. */
export const AMORTISSEMENT_BASE = 3.4;
/** Amplitude de tremblement à agressivité maximale, en points d'aiguille. */
export const TREMBLEMENT_MAX = 4.2;
/** Multiplicateur de tremblement en zone rouge de santé mentale (A-3.1 point 2). */
export const TREMBLEMENT_ZONE_ROUGE = 1.5;
/** Drift du sweet spot (points par seconde) à partir de E2. */
export const DRIFT_PAR_SECONDE = 1.6;

/**
 * Période du tremblement, en ms — recopiée de `server/src/atelier/scoring.ts`.
 * **Client et serveur doivent trembler pareil** : c'est le tremblement à l'instant du gel qui
 * entre dans le score (A-3.1 pt 3), donc une phase ou une période différentes suffiraient à
 * faire diverger les deux de plusieurs points, sans que rien ne le signale.
 */
export const TREMBLEMENT_PERIODE_MS = 320;

/**
 * **Le pas d'intégration de la physique de l'aiguille, en ms — et c'est un contrat.**
 *
 * À pas variable (`requestAnimationFrame`), client et serveur ne tomberaient jamais sur la même
 * position figée : le joueur verrait 94 là où le serveur écrirait 91. Le serveur intègre à pas
 * fixe et **transmet ce pas dans le pattern** ; en bac à sable, on prend la même valeur.
 */
export const PAS_PHYSIQUE_MS = 1000 / 120;

/** L'état neutre des curseurs — celui d'où le serveur part quand la trace ne dit rien encore. */
export const VALEUR_NEUTRE = 50;

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-69⟧ « La promesse » — jumeau NON AUTORITATIF du serveur
// ─────────────────────────────────────────────────────────────────────────────
// Jumeau de `server/src/atelier/constantes.ts` (bloc `PROMESSE_*`) et de
// `server/src/atelier/scoring.ts#{promesseTenue,noterPromesse}`. Le serveur note ; ce module
// ne sert qu'à ce que l'écran dise la même chose que lui.

export const PROMESSE_CHRONO_BASE_MS = 8_000;
export const PROMESSE_LARGEUR_MIN_MULT = 0.6;
export const PROMESSE_LARGEUR_MAX_MULT = 1.8;
export const PROMESSE_LARGEUR_DEPART_MULT = 1.1;
export const PROMESSE_PRECISION_PLANCHER = 0.35;
export const PROMESSE_PENALITE_MENSONGE = 0.5;
export const PROMESSE_POIDS = 0.25;

/** La phase d'annonce, ou `null` quand le nœud n'est pas acheté. */
export interface Promesse {
  chronoMs: number;
  largeurMin: number;
  largeurMax: number;
  departCentre: number;
  departLargeur: number;
  seuilMensonge: number;
}

/** La fourchette que le joueur a fini par promettre. */
export interface Fourchette {
  centre: number;
  largeur: number;
}

export interface Curseur {
  id: string;
  /** Extrémité basse (0) et haute (100) — le vocabulaire de l'avenant A-3. */
  gauche: string;
  droite: string;
  /** Poids **caché** dans la combinaison qui pilote l'aiguille. */
  poids: number;
  /**
   * Couplage (hauts paliers, A-3.1 point 5) : bouger ce curseur déplace le sweet spot de
   * `couplage` point par point de curseur. 0 = pas de couplage.
   */
  couplage: number;
}

export interface PatternPutaclic {
  curseurs: Curseur[];
  chronoMs: number;
  /** Centre du sweet spot au départ (il peut driver ensuite). */
  centre: number;
  /** Largeur TOTALE de la zone sweet. */
  zone: number;
  /** Frontière basse de la zone rouge « mensongère » — jamais affichée avant de la franchir. */
  seuilMensonge: number;
  /** ⟦A-69⟧ La phase d'annonce, ou `null` : c'est le seul interrupteur. */
  promesse: Promesse | null;
  /** Points par seconde de dérive du sweet spot (0 aux paliers bas). */
  drift: number;
  raideur: number;
  amortissement: number;
  /** Amplitude réelle du tremblement (celle qui entre dans le score figé). */
  tremblement: number;
  /** Phase du tremblement, seedée : le serveur et le client doivent trembler pareil. */
  tremblementPhase: number;
  /** Bruit ajouté à l'AFFICHAGE seulement — *Analytics addict* le réduit. */
  bruitAffichage: number;
  /** Pas d'intégration **imposé** de la physique de l'aiguille. */
  pasPhysiqueMs: number;
  scoreCap: number;
  k: number;
  /** ⟦A-83⟧ Mode posé (SPEC-UI §7.4) : pas de temps, curseur pas-à-pas, Entrée pour valider. */
  pose: boolean;
}

export interface OptionsPutaclic {
  state: GameState;
  seed: string;
  /** Alignement du sujet : un sujet chaos a une zone plus étroite (SPEC-UI §4.2). */
  alignementSujet: number;
  /** *Miniature putaclic* : zone sweet +20 %. */
  zoneBonus?: number;
  /** *Plume d'or* : amortisseur, tremblement ÷2. */
  amortisseur?: boolean;
  /** *Analytics addict* : jauge moins bruitée. */
  jaugeLisible?: boolean;
  /** Zone rouge de santé mentale : tremblement ×1,5. */
  zoneRougeSM?: boolean;
  /** ⟦A-69⟧ *MAJUSCULES* : la phase « La promesse » précède le dosage. */
  phasePromesse?: boolean;
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (§7.4) — jumelle du serveur. */
  pose?: boolean;
}

/**
 * ⟦A-83 · SPEC-UI §7.4⟧ Mode posé : « pas de temps ». Le chrono est **triplé**, jamais
 * affiché, jamais tranchant — le joueur valide. Jumeau de `POSE_CHRONO_MULT_SANS_TEMPS` côté
 * serveur (`server/src/atelier/constantes.ts`).
 */
export const POSE_CHRONO_MULT = 3;

/** La promesse du bac à sable — jumeau de `GenerateurPatterns#promesse`. */
function promesseLocale(
  centre: number,
  zone: number,
  seuilMensonge: number,
  tempoMult: number,
  pose = false,
): Promesse {
  return {
    chronoMs: Math.max(3_500, Math.round(PROMESSE_CHRONO_BASE_MS / tempoMult)) * (pose ? POSE_CHRONO_MULT : 1),
    largeurMin: zone * PROMESSE_LARGEUR_MIN_MULT,
    largeurMax: zone * PROMESSE_LARGEUR_MAX_MULT,
    departCentre: centre,
    departLargeur: zone * PROMESSE_LARGEUR_DEPART_MULT,
    seuilMensonge,
  };
}

/** Les trois curseurs de l'avenant A-3, dans l'ordre où ils s'ouvrent. */
const CURSEURS_POSSIBLES: readonly Omit<Curseur, "poids" | "couplage">[] = [
  { id: "expression", gauche: "sobre", droite: "choquée" },
  { id: "promesse", gauche: "honnête", droite: "énorme" },
  { id: "couleurs", gauche: "classe", droite: "flashy" },
];

/**
 * Génère le réglage. Déterministe : `(seed, turn, "miniature")` ⇒ mêmes poids, même sweet
 * spot, même frontière rouge. Rejouer le tour ne re-roule rien (PLAN §6).
 */
export function genererReglage(options: OptionsPutaclic): PatternPutaclic {
  const { state, seed } = options;
  const exigence = exigenceAtelier(state);
  const graine = generateAtelierPattern(state, "miniature", seed).seed_component;
  const u = (i: number) => draw(graine, 0, "atelier_pattern", i);

  // 2 curseurs aux paliers bas, le 3e à partir de E2 (A-3 « scaling »).
  const combien = exigence.k >= 2 ? 3 : 2;
  // Poids cachés : normalisés pour que « tout à fond » sature l'aiguille à 100.
  const bruts = CURSEURS_POSSIBLES.slice(0, combien).map((_, i) => uniformFloat(u(i), 0.6, 1.4));
  const somme = bruts.reduce((a, b) => a + b, 0);
  const curseurs: Curseur[] = CURSEURS_POSSIBLES.slice(0, combien).map((c, i) => ({
    ...c,
    poids: bruts[i]! / somme,
    // Couplage à partir de E4 : bouger un curseur déplace le sweet spot d'un autre.
    couplage: exigence.k >= 4 && i > 0 ? uniformFloat(u(10 + i), -0.12, 0.12) : 0,
  }));

  const chaos = options.alignementSujet <= -30;
  const zone =
    ZONE_BASE * exigence.window_mult * (chaos ? ZONE_CHAOS : 1) * (options.zoneBonus ?? 1);

  const modeDoux = state.subscribers < 100;
  const chronoMs = Math.round(CHRONO_BASE_MS / (exigence.tempo_mult * (modeDoux ? 0.9 : 1)));

  const centre = uniformFloat(u(20), CENTRE_MIN, CENTRE_MAX);
  const seuilMensonge = uniformFloat(u(21), MENSONGE_MIN, MENSONGE_MAX);

  // §7.4 posé : « pas de temps » — le chrono est triplé et jamais montré ; l'aiguille ne
  // dérive plus et ne tremble plus. Jumeau du serveur (`POSE_CHRONO_MULT_SANS_TEMPS`).
  const pose = options.pose === true;
  return {
    curseurs,
    chronoMs: chronoMs * (pose ? POSE_CHRONO_MULT : 1),
    centre,
    zone,
    seuilMensonge,
    promesse: options.phasePromesse
      ? promesseLocale(centre, zone, seuilMensonge, exigence.tempo_mult, pose)
      : null,
    drift: !pose && exigence.k >= 2 ? DRIFT_PAR_SECONDE * (1 + 0.2 * (exigence.k - 2)) : 0,
    raideur: RAIDEUR_BASE * exigence.tempo_mult,
    // L'amortissement BAISSE quand l'exigence monte : l'aiguille devient nerveuse.
    amortissement: AMORTISSEMENT_BASE / exigence.tempo_mult,
    tremblement: pose
      ? 0
      : TREMBLEMENT_MAX * (options.zoneRougeSM ? TREMBLEMENT_ZONE_ROUGE : 1) *
        (options.amortisseur ? 0.5 : 1),
    tremblementPhase: uniformFloat(u(22), 0, Math.PI * 2),
    bruitAffichage: options.jaugeLisible ? 0.6 : 1.8,
    pasPhysiqueMs: PAS_PHYSIQUE_MS,
    scoreCap: exigence.score_cap,
    k: exigence.k,
    pose,
  };
}

/**
 * Le pattern **servi par le serveur**, traduit tel quel : rien n'est recalculé ici.
 *

 * ⚠️ **Le `scoreCap` du fil n'est PAS celui du barème d'écran.**
 *
 * L'enveloppe du serveur porte `exigenceAtelier(état, étape).score_cap`, qui **fusionne** deux
 * bornes : la tolérance d'accessibilité (85) et le **plafond de compétence** ⟦A-9⟧ de la
 * branche (55 au socle). Or le moteur applique déjà le second à la publication
 * (`scoresEffectifs`), et l'écran de l'Atelier le montre à part — « 92 · plafonné à 45, ton
 * niveau ». Le laisser raboter le score du mini-jeu, ce serait l'appliquer **deux fois** et
 * saboter le geste, ce que l'avenant interdit explicitement : « le joueur voit son vrai geste,
 * et voit ce que son niveau en retient ».
 *
 * Le barème local ne retient donc que la borne qui ne dépend de rien, lue du moteur —
 * exactement ce que fait la génération en bac à sable. Le serveur note pareil : son
 * `validate.ts` ne connaît que `TOLERANCE_SCORE_CAP`.
 */
export function depuisServeur(api: PatternMiniatureApi, state: GameState): PatternPutaclic {
  return {
    curseurs: api.params.curseurs.map((c) => ({ ...c })),
    chronoMs: api.params.chronoMs,
    centre: api.params.centre,
    zone: api.params.zone,
    seuilMensonge: api.seuilMensonge,
    promesse: api.params.promesse ? { ...api.params.promesse } : null,
    drift: api.params.drift,
    raideur: api.params.raideur,
    amortissement: api.params.amortissement,
    tremblement: api.params.tremblement,
    tremblementPhase: api.params.tremblementPhase,
    bruitAffichage: api.params.bruitAffichage,
    pasPhysiqueMs: api.params.pasPhysiqueMs,
    scoreCap: exigenceAtelier(state).score_cap,
    k: api.tier,
    // ⟦A-83⟧ Le serveur a généré la variante posée si on la lui a demandée (§7.4).
    pose: api.reducedMotion,
  };
}

/** Cible de l'aiguille pour un état de curseurs (0-100 chacun) — la combinaison cachée. */
export function cibleAiguille(pattern: PatternPutaclic, valeurs: readonly number[]): number {
  let total = 0;
  pattern.curseurs.forEach((c, i) => {
    total += (valeurs[i] ?? 0) * c.poids;
  });
  return Math.max(0, Math.min(100, total));
}

/**
 * Centre du sweet spot à l'instant `tMs`, couplages compris (A-3.1 point 5). Le drift est
 * une oscillation lente, pas une fuite linéaire : la zone reste atteignable.
 */
export function centreCourant(
  pattern: PatternPutaclic,
  tMs: number,
  valeurs: readonly number[],
  centreBase?: number,
): number {
  const derive = pattern.drift === 0 ? 0 : Math.sin((tMs / 1000) * 0.45) * pattern.drift * 4;
  let couplage = 0;
  pattern.curseurs.forEach((c, i) => {
    couplage += ((valeurs[i] ?? 0) - 50) * c.couplage;
  });
  return Math.max(10, Math.min(95, (centreBase ?? pattern.centre) + derive + couplage));
}

/**
 * Un pas de physique de l'aiguille : ressort amorti vers la cible. Rend la nouvelle position
 * et la nouvelle vitesse. `dt` en secondes.
 *
 * C'est l'idée de Mathieu (A-3.1 point 2) : l'aiguille **répond avec retard et overshoot**.
 * L'amortissement est sous-critique (< 2√raideur), donc elle dépasse et revient — d'où
 * l'intérêt de doser TÔT et de laisser l'inertie s'amortir avant que le chrono ne fige.
 */
export function pasAiguille(
  pattern: PatternPutaclic,
  position: number,
  vitesse: number,
  cible: number,
  dt: number,
): { position: number; vitesse: number } {
  const acceleration = (cible - position) * pattern.raideur - vitesse * pattern.amortissement;
  const v = vitesse + acceleration * dt;
  return { position: position + v * dt, vitesse: v };
}

/**
 * Amplitude du tremblement à cet instant : elle **croît avec l'agressivité du réglage**
 * (A-3.1 point 2 — « plus tu pousses le putaclic, plus l'aiguille monte haut… et plus elle
 * est nerveuse »).
 */
export function amplitudeTremblement(pattern: PatternPutaclic, cible: number): number {
  return pattern.tremblement * (0.25 + 0.75 * (cible / 100));
}

/**
 * Le tremblement à l'instant `tMs` — **déterministe**, phase seedée dans le pattern.
 *
 * Copie conforme de `server/src/atelier/scoring.ts#tremblement`. C'est lui qui est figé à la fin
 * du chrono et qui entre dans le score : une formule différente de chaque côté, et l'écran et la
 * base ne parleraient pas de la même aiguille.
 */
export function tremblement(pattern: PatternPutaclic, tMs: number, cible: number): number {
  return (
    amplitudeTremblement(pattern, cible) *
    Math.sin((2 * Math.PI * tMs) / TREMBLEMENT_PERIODE_MS + pattern.tremblementPhase)
  );
}

/**
 * **Le score, refait depuis la trace des réglages** — copie conforme de
 * `server/src/atelier/scoring.ts#scorerMiniature`.
 *
 * L'intégration part de l'état **neutre** (tous les curseurs à 50) et avance à **pas fixe**
 * jusqu'au chrono, en lisant à chaque pas le dernier réglage soumis. C'est exactement ce que le
 * serveur refera : le jouer autrement ici, c'était accepter que l'écran et la base annoncent
 * deux aiguilles différentes.
 */
export function noterTraceReglage(
  pattern: PatternPutaclic,
  reglages: TimelineMiniature["reglages"],
  soumise: TimelineMiniature["promesse"] = undefined,
): ResultatPutaclic {
  // ⟦A-69⟧ La promesse d'abord : c'est elle qui décide OÙ est le sweet spot et COMBIEN il est
  // large. À `null`, tout retombe sur les nombres tirés — au centième près.
  const fourchette = promesseTenue(pattern, soumise);
  const nb = pattern.curseurs.length;
  const neutre = new Array<number>(nb).fill(VALEUR_NEUTRE);
  const tries = [...reglages].sort((a, b) => a.atMs - b.atMs);

  const valeursA = (tMs: number): number[] => {
    let courant = neutre;
    for (const r of tries) {
      if (r.atMs > tMs) break;
      courant = Array.from({ length: nb }, (_, i) =>
        Math.max(0, Math.min(100, r.valeurs[i] ?? VALEUR_NEUTRE)),
      );
    }
    return courant;
  };

  const pas = pattern.pasPhysiqueMs > 0 ? pattern.pasPhysiqueMs : PAS_PHYSIQUE_MS;
  const dt = pas / 1000;
  let position = cibleAiguille(pattern, neutre);
  let vitesse = 0;

  for (let t = 0; t < pattern.chronoMs; t += pas) {
    const cible = cibleAiguille(pattern, valeursA(t));
    const suivant = pasAiguille(pattern, position, vitesse, cible, dt);
    position = Math.max(-20, Math.min(120, suivant.position)); // garde-fou, comme le serveur
    vitesse = suivant.vitesse;
  }

  const valeursFinales = valeursA(pattern.chronoMs);
  const cibleFinale = cibleAiguille(pattern, valeursFinales);
  const aiguille = Math.max(
    0,
    Math.min(100, position + tremblement(pattern, pattern.chronoMs, cibleFinale)),
  );
  const centre = centreCourant(
    pattern,
    pattern.chronoMs,
    valeursFinales,
    centreApresPromesse(pattern, fourchette),
  );
  return noterPutaclic(pattern, aiguille, centre, fourchette);
}

/**
 * ⟦A-69⟧ La fourchette finale — jumeau de `scoring.ts#promesseTenue`.
 *
 * Rend `null` sans la phase : les appelants retombent sur `pattern.centre` et `pattern.zone`,
 * **les mêmes nombres**.
 */
export function promesseTenue(
  pattern: PatternPutaclic,
  soumise: TimelineMiniature["promesse"],
): Fourchette | null {
  const plan = pattern.promesse;
  if (!plan) return null;

  const fin = Math.max(0, Math.min(plan.chronoMs, soumise?.finishedAtMs ?? 0));
  const poses = [...(soumise?.poses ?? [])].sort((a, b) => a.atMs - b.atMs);

  let centre = plan.departCentre;
  let largeur = plan.departLargeur;
  for (const p of poses) {
    if (p.atMs > fin) continue;
    centre = p.centre;
    largeur = p.largeur;
  }
  return {
    centre: Math.max(0, Math.min(100, centre)),
    largeur: Math.max(plan.largeurMin, Math.min(plan.largeurMax, largeur)),
  };
}

/** ⟦A-50⟧ La fourchette promise **devient** la zone à tenir. Sans phase : `pattern.zone`. */
export function zoneApresPromesse(pattern: PatternPutaclic, p: Fourchette | null): number {
  return p === null ? pattern.zone : p.largeur;
}

/** Le centre à viser : celui qu'on a promis, ou celui qu'on a tiré. */
export function centreApresPromesse(pattern: PatternPutaclic, p: Fourchette | null): number {
  return p === null ? pattern.centre : p.centre;
}

export interface NotePromesse {
  score: number;
  ambition: number;
  precision: number;
  /** ⟦A-59⟧ Ce que le dosage a réellement fait de la promesse. */
  tenue: number;
  mensonger: boolean;
}

/**
 * ⟦A-69⟧ La note de la promesse — jumeau de `scoring.ts#noterPromesse`, à la virgule.
 *
 * Promettre **haut** rapporte et fait trembler ; promettre **serré** rapporte et rétrécit la
 * cible ; et le **mensonge se juge sur le résultat**, pas sur la promesse — sinon il suffirait
 * de s'arrêter un point sous la ligne rouge, qui est dessinée à l'écran.
 */
export function noterPromesse(
  pattern: PatternPutaclic,
  p: Fourchette | null,
  aiguille: number,
  dosage: number,
): NotePromesse | null {
  const plan = pattern.promesse;
  if (!plan || p === null) return null;

  const span = Math.max(1e-9, plan.largeurMax - plan.largeurMin);
  const precision = Math.max(0, Math.min(1, (plan.largeurMax - p.largeur) / span));
  // Le HAUT de la fourchette, rapporté à la frontière rouge : au-delà on ne promet plus, on ment.
  const ambition = Math.max(
    0,
    Math.min(1, (p.centre + p.largeur / 2) / Math.max(1e-9, plan.seuilMensonge)),
  );
  const tenue = Math.max(0, Math.min(1, dosage / 100));
  const mensonger = aiguille >= plan.seuilMensonge;

  const brut =
    100 *
    ambition *
    (PROMESSE_PRECISION_PLANCHER + (1 - PROMESSE_PRECISION_PLANCHER) * precision) *
    tenue *
    (mensonger ? 1 - PROMESSE_PENALITE_MENSONGE : 1);

  return { score: Math.max(0, Math.min(100, brut)), ambition, precision, tenue, mensonger };
}

export interface ResultatPutaclic {
  score: number;
  /** Position figée de l'aiguille, tremblement compris (A-3.1 point 3). */
  aiguille: number;
  centre: number;
  /** Dans la zone sweet ? */
  dansLaZone: boolean;
  /** Figé dans la zone rouge : flag « putaclic mensonger » (poids du bad buzz ×2). */
  mensonger: boolean;
  ecart: number;
  /** ⟦A-69⟧ Le score du dosage seul, avant composition — `null` sans la phase. */
  dosage: number | null;
  /** ⟦A-69⟧ La note de la promesse, ou `null` sans la phase. */
  promesse: NotePromesse | null;
}

/**
 * Le barème. Même forme que celui du Montage (SPEC-UI §5.3) pour que les deux jeux se
 * ressentent pareil : plein pot au cœur de la zone, dégressif jusqu'au bord, puis chute.
 *
 * ⚠️ Non autoritatif : le scoring qui compte est **serveur** (M2.9). Ici il n'y a pas de
 * serveur — d'où une fonction pure, isolée, qu'on jettera sans toucher au jeu.
 */
export function noterPutaclic(
  pattern: PatternPutaclic,
  aiguille: number,
  centre: number,
  fourchette: Fourchette | null = null,
): ResultatPutaclic {
  const largeur = zoneApresPromesse(pattern, fourchette);
  const demi = largeur / 2;
  const quart = largeur / 4;
  const ecart = Math.abs(aiguille - centre);

  let brut: number;
  if (ecart <= quart) brut = 100;
  else if (ecart <= demi) brut = 100 - 60 * ((ecart - quart) / quart);
  else brut = Math.max(0, 40 - (ecart - demi) * 2);
  const dosage = Math.max(0, Math.min(100, brut));

  // ⟦A-69⟧ Sans promesse (`plan === null`), la ligne suivante rend `dosage` — au centième près.
  const plan = noterPromesse(pattern, fourchette, aiguille, dosage);
  const compose = plan === null ? dosage : (1 - PROMESSE_POIDS) * dosage + PROMESSE_POIDS * plan.score;

  return {
    score: Math.max(0, Math.min(pattern.scoreCap, Math.round(compose))),
    aiguille,
    centre,
    dansLaZone: ecart <= demi,
    mensonger: aiguille >= pattern.seuilMensonge,
    ecart,
    dosage: plan === null ? null : dosage,
    promesse: plan,
  };
}
